import React, { FC } from 'react';

import { FormattedMessage } from 'react-intl';
import { useTheme } from 'styled-components';

import CancelRoundedIcon from '@mui/icons-material/CancelRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import { ShortCodeEnums } from '@nhl/nhl-breakaway-ui';
import Headshot from '@nhl/nhl-react-ui/lib/Headshot';
import headshotTypes from '@nhl/nhl-react-ui/lib/Headshot/headshotTypes';
import { TeamDisplay } from '@nhl/nhl-react-ui/lib/TeamDisplay';

import commonMessages from '~/core/commonMessages';
import { getPlayerHref, parseValueFromLocalizedAttribute } from '~/core/utils';
import VideoClipPlayer from '~/views/components/videoClipPlayer/VideoClipPlayer';
import { useGoalHighlightClip } from '~/views/components/videoClipPlayer/utils';

import { PptReplayTeam as Team } from '../../../../../pptReplayEmbed/types';
import { GameCenterTeam, ShootoutAttempt as ShootoutAttemptType } from '../../../../types';

import {
    AssistInformation,
    GoalScorerContainer,
    PlayerAndInfoContainer,
    PlayerInfo,
    PlayerInfoContainer,
    PlayerName,
    ShootoutResultContainer,
    TopRow,
} from './styles';

type Props = {
    shootoutAttempt: ShootoutAttemptType;
    logoUrl: string;
    isWebview: boolean;
    isLarge: boolean;
    gameState: ShortCodeEnums.GameState | null;
    homeTeam: GameCenterTeam | Team;
    awayTeam: GameCenterTeam | Team;
};

const GOAL_RESULT = 'goal';

const ShootoutAttempt: FC<Props> = ({ shootoutAttempt, logoUrl, isWebview, gameState, homeTeam, awayTeam }) => {
    const theme = useTheme();
    const isGoal = shootoutAttempt.result === GOAL_RESULT;
    const highlightClip = useGoalHighlightClip(shootoutAttempt);
    const isGameWinner = shootoutAttempt.gameWinner;

    return (
        <GoalScorerContainer>
            <PlayerAndInfoContainer>
                <TopRow>
                    <PlayerInfoContainer>
                        <Headshot
                            type={headshotTypes.SKATER}
                            urlOverride={shootoutAttempt.headshot}
                            size={64}
                            backgroundColor={
                                shootoutAttempt.teamAbbrev
                                    ? theme.Team[shootoutAttempt.teamAbbrev.default]?.gamecenter
                                    : theme.Surface.secondary
                            }
                        />
                        <PlayerInfo>
                            <PlayerName href={getPlayerHref(shootoutAttempt.playerId, isWebview)} rel="noreferrer">
                                {shootoutAttempt.firstName && shootoutAttempt.lastName && (
                                    <>
                                        {parseValueFromLocalizedAttribute(shootoutAttempt.firstName)}
                                        &nbsp;
                                        {parseValueFromLocalizedAttribute(shootoutAttempt.lastName)}
                                    </>
                                )}
                            </PlayerName>
                            <AssistInformation>
                                <TeamDisplay
                                    logoUrlOverride={logoUrl}
                                    team={{ triCode: shootoutAttempt.teamAbbrev.default }}
                                    displayName={false}
                                    useInTable
                                />
                                {isGameWinner && (
                                    <FormattedMessage {...commonMessages.eventDescriptions['game-winning-goal']} />
                                )}
                                {isGoal && !isGameWinner && (
                                    <FormattedMessage
                                        description="A goal scored during the shootout of a game."
                                        defaultMessage="Goal"
                                    />
                                )}
                                {!isGoal && (
                                    <FormattedMessage
                                        description="A save during the shootout of a game."
                                        defaultMessage="Save"
                                    />
                                )}
                            </AssistInformation>
                        </PlayerInfo>
                    </PlayerInfoContainer>
                    <ShootoutResultContainer $isGoal={isGoal}>
                        {isGameWinner && highlightClip !== undefined && (
                            <VideoClipPlayer
                                homeTeam={homeTeam}
                                awayTeam={awayTeam}
                                goal={shootoutAttempt}
                                gameState={gameState}
                            />
                        )}
                        {isGoal && (!isGameWinner || !highlightClip) && <CheckCircleRoundedIcon />}
                        {!isGoal && <CancelRoundedIcon />}
                    </ShootoutResultContainer>
                </TopRow>
            </PlayerAndInfoContainer>
        </GoalScorerContainer>
    );
};

export default ShootoutAttempt;
